<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading || status.isLoadingStripeElements"
    :loading-message="spinnerMessage"
    :disabled="isSubmitBtnDisabled"
    :no-action="!showCardInfo"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Add Credit or Debit Card
    </template>

    <template #card-notes>
      <div
        v-if="!isSelfPayForm && isFamilyDashboard && !activePlanName && selectedPlan"
        class="card-notes"
      >
        <p class="notification-message">
          Please keep in mind, you will be starting {{ selectedPlan }} membership.
        </p>
      </div>
    </template>

    <template #content>
      <template v-if="showCardInfo">
        <div v-if="isPartnerDashboard">
          <div class="row align-items-center mb-20">
            <label class="col-sm-3 control-label"> Name </label>
            <div
              v-if="customerInfo"
              class="col-sm-8"
            >
              {{ customerFullName }}
            </div>
          </div>
          <div class="row align-items-center mb-20">
            <label class="col-sm-3 control-label"> Caller ID </label>
            <div class="col-sm-8">
              {{ cardInfo.callerId }}
            </div>
          </div>
        </div>

        <div
          v-if="isFamilyDashboard || isPartnerDashboard"
          class="row pt-10"
        >
          <label class="col-sm-3 control-label"> Plan </label>
          <div class="col-sm-8">
            <div v-show="activePlanName">
              {{ activePlanName }}
              <span class="help-block"> This is the user's current membership plan </span>
            </div>
            <div v-show="!activePlanName">
              <dropdown-list
                v-model="selectedPlan"
                :options="planList"
              />
              <span
                v-if="registerPlan && registerPlan === normalizePlanName"
                class="help-block"
              >
                The selected plan is what the user chose online when they registered
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label"> Card Holder Name* </label>
          <div class="col-sm-8">
            <sanitized-input
              id="holderName"
              v-model="cardInfo.holderName"
              required
            />
            <span class="help-block"> The full name as it appears on the card </span>
          </div>
        </div>

        <div
          v-if="isPartnerDashboard"
          class="row align-items-center card-holder-phone"
        >
          <label class="col-sm-3 control-label"> Card Holder Phone Number </label>
          <phone-number-input
            id="card-holder-phone"
            v-model="cardInfo.holderPhoneNumber"
            class="col-sm-8"
            :page-id="pageId"
            :required="false"
          />
        </div>

        <div
          v-if="!isSelfPayForm"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Use 3D Secure </label>
          <div class="col-sm-8">
            <el-checkbox v-model="use3ds">
              {{ use3ds ? 'Yes' : 'No' }}
            </el-checkbox>
          </div>
        </div>

        <stripe-elements />

        <address-section
          v-model="address"
          class="mt-4"
          :page-id="pageId"
          :required="true"
          :show-home-address-btn="!!customerInfo.id"
          @set-home-address="setHomeAddress"
        >
          <template #streetAddressLbl>
            Billing Address *
          </template>
        </address-section>
      </template>

      <div
        v-if="showInlineError"
        v-dompurify-html="result.message"
        class="result"
        :class="result.type"
      />
    </template>

    <template #submit-button>
      Add Card
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, watch, toRefs, onMounted } from 'vue';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { BillingDetails, StripeChangeEvent, useStripe } from '@/use/stripe';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';
import { CustomLocationType } from '@/models/address';
import { getStripeErrorMessageHtml } from '@/services/payments/stripeErrors';
import { paymentService } from '@/services/payment';
import { callerService } from '@/services/caller';
import { pushNotificationService } from '@/services/pushNotification';
import { PushNotificationCode } from '@/models/app';




const pageId = 'add-credit-card';

const props = defineProps<{
  // Membership to start once the card is saved. Falls back to the customer's last plan when omitted.
  plan?: string;
}>();

const { initStripe, terminateStripe, clearStripe, confirmSetup, currentSetupIntentId, handleStripeError } = useStripe();
const { customerInfo, customerFullName, customerActivePlan, getFormattedPlanList } = useCustomer();

const { userRoles } = useProfile();
const { isAdmin, isSupervisor, isTeamLead, isDirector, isCustomerUser, isFamilyUser } = toRefs(userRoles);
const { isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { trimAddress } = useAddress();

const cardInfo: Ref<any> = ref({
  callerId: '',
  holderName: '',
  holderPhoneNumber: '',
});
// FIXME: Change this to BillingAddress once this PR is merged!
// https://bitbucket.org/gogograndparent/gogo-web/pull-requests/1905/overview
const address: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress2: '',
  country: '',
  city: '',
  state: '',
  zip: '',
});

const status = reactive({
  isLoadingStripeElements: true,
  isLoading: false,
  isComplete: false,
  isReset: false,
  isCardAdded: false,
});
const spinnerMessage = ref('');
const selectedPlan: Ref<any> = ref(null);
const use3ds = ref(false);
const result = ref({
  message: '',
  operatorNotificationError: '',
  type: 'success',
});

const hasSpecialPermission = computed(() => isAdmin.value || isSupervisor.value || isDirector.value || isTeamLead.value);

const isSubmitBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (!cardInfo.value.callerId) {
    return true;
  }

  if (!status.isComplete) {
    return true;
  }

  return false;
});

const planList = getFormattedPlanList(hasSpecialPermission.value);

const activePlanName = computed(() => {
  const planName = util.getLabelByField(planList.value, customerActivePlan.value?.planName ?? '');

  if (!planName) {
    return '';
  }

  return planName;
});

const registerPlan = computed(() => util.normalizePlanName(customerInfo.value?.registerPlan));
const normalizePlanName = computed(() => util.normalizePlanName(selectedPlan.value));
const isSelfPayForm = computed(() => !isFamilyDashboard.value && !isPartnerDashboard.value);
const showInlineError = computed(() => isSelfPayForm.value && !!result.value.message)
const showCardInfo = computed(() => !isSelfPayForm.value || !status.isCardAdded);

const resetStripeElement = async () => {
  status.isLoadingStripeElements = true;
  status.isCardAdded = false;
  terminateStripe();
  try {
    await initStripe({
      onChange: (events: StripeChangeEvent) => {
        changeStripeElements(events.complete);
      },
      onReady: () => {
        status.isLoadingStripeElements = false;
      },
      use3ds: use3ds.value,
      isCustomerSelfPay: isSelfPayForm.value,
      stripeAccountName: customerInfo.value?.stripeAccountName,
      callerId: cardInfo.value.callerId || undefined,
    });
  } catch (error) {
    result.value = {
      message: handleStripeError(error, false),
      operatorNotificationError: '',
      type: 'error',
    };
    !showInlineError.value && showErrorPopupDialog({
      text: `An error occurred while loading the payment form. Please try again.</br></br> ${result.value.message}`,
      type: 'html',
    });
    terminateStripe();
    sendPushNotification();
  }
};

// event from stripe element to check stripe elements are completed or not
const changeStripeElements = (isComplete: boolean) => {
  status.isComplete = isComplete;
};

const submitForm = async () => {
  let confirmText = 'Confirm adding this card to the account.';

  if (selectedPlan.value && !customerActivePlan.value) {
    confirmText += ` Please remind the customer that they will be starting ${selectedPlan.value} membership.`;
  }

  if (isSelfPayForm.value) {
    confirmText = 'You will only be charged for the services you use and membership plan you select.';
  }

  const isConfirmed = await showConfirmPopupDialog({
    text: confirmText,
    title: isSelfPayForm.value ? 'Add Card' : 'Confirm',
    confirmButtonText: 'Confirm',
  });

  if (!isConfirmed) {
    return;
  }

  address.value = trimAddress(address.value);
  cardInfo.value.holderPhoneNumber = util.getValidPhoneNumber(cardInfo.value.holderPhoneNumber);

  const billingDetails: BillingDetails = {
    name: cardInfo.value.holderName?.trim(),
    address: {
      line1: address.value.streetAddress,
      line2: address.value.streetAddress2 ?? '',
      city: address.value.city,
      state: address.value.state,
      postal_code: address.value.zip,
      country: address.value.country!,
    },
  };

  if (isPartnerDashboard.value && cardInfo.value.holderPhoneNumber) {
    billingDetails.phone = cardInfo.value.holderPhoneNumber;
  }

  spinnerMessage.value = 'Checking Card Info...';
  status.isLoading = true;

  try {
    const { setupIntentId, error, confirmationTokenId } = await confirmSetup(billingDetails);
    status.isLoading = false;

    if (!error) {
      await checkAndAddCreditCard(setupIntentId, confirmationTokenId);
    } else {
      void paymentService.handleSetupIntentError({
        setupIntentId,
        userId: customerInfo.value!.id,
      });

      resetForm();

      result.value = {
        message: isPartnerDashboard.value ? getStripeErrorMessageHtml(error) : error.message,
        operatorNotificationError: getStripeErrorMessageHtml(error),
        type: 'error',
      };
      !showInlineError.value && showErrorPopupDialog({
        text: result.value.message,
        type: 'html',
      });

      sendPushNotification();
    }
  } catch (error) {
    result.value = {
      message: handleStripeError(error, false),
      operatorNotificationError: '',
      type: 'error',
    };
    !showInlineError.value && showErrorPopupDialog({
      text: `An error occurred while processing your payment. Please try again.</br></br>${result.value.message}</br>SetupIntentId: ${currentSetupIntentId()}`,
      type: 'html',
    });

    sendPushNotification();
  } finally {
    status.isLoading = false;
  }
};

const checkAndAddCreditCard = async (setupIntentId: string, confirmationTokenId?: string) => {
  // The spinnerMessage is not actually being updated, unfortunately
  // https://github.com/element-plus/element-plus/issues/14926
  status.isLoading = true;
  spinnerMessage.value = 'Checking Credit Card...';

  try {
    await paymentService.checkPaymentSource({
      setupIntentId,
      confirmationTokenId,
      userId: cardInfo.value.callerId,
    });

    await addCreditCard(setupIntentId, confirmationTokenId);
  } catch (err) {
    status.isLoading = false;

    const errorCode = util.getGraphqlErrorCode(err);
    if (errorCode === 'noPrepaidCards') {
      let errorHtml = '<strong>Card Type</strong>: Prepaid<br />';
      errorHtml = `${errorHtml}<strong>Notice</strong>: We are unable to save prepaid cards to a customers account.<br />`;
      errorHtml = `${errorHtml}<strong>Solution</strong>: Add GoGo Credits with the prepaid card, on the GoGo Credits Tab.`;

      result.value = {
        message: errorHtml,
        operatorNotificationError: '',
        type: 'error',
      };
      !showInlineError.value && showErrorPopupDialog({
        text: result.value.message,
        type: 'html',
      });

      resetStripeElement();
      return;
    }

    resetForm();
    resetStripeElement();

    result.value = {
      message: util.getGraphqlError(err, false),
      operatorNotificationError: '',
      type: 'error',
    };
    !showInlineError.value && showErrorPopupDialog({
      text: result.value.message,
    });

    sendPushNotification();
  }
};

const addCreditCard = async (setupIntentId: string, confirmationTokenId?: string) => {
  spinnerMessage.value = 'Adding Credit Card...';
  status.isLoading = true;

  const postData = {
    setupIntentId,
    confirmationTokenId,
    stripeAccountName: customerInfo.value?.stripeAccountName,
    userId: cardInfo.value.callerId,
    plan: customerActivePlan.value ? null : selectedPlan.value,
  };

  try {
    let visitorId = undefined;

    if (isCustomerUser.value || isFamilyUser.value) {
      visitorId = await callerService.getVisitorId();
    }

    await paymentService.addPaymentSource({
      ...postData,
      visitorId,
    });

    status.isCardAdded = true;
    result.value = {
      message: isSelfPayForm.value ? 'Your card has been added successfully.' : 'The credit card has been added successfully.',
      operatorNotificationError: '',
      type: 'success',
    };
    !showInlineError.value && showSuccessPopupDialog({
      text: result.value.message,
    });

    if (isPartnerDashboard.value) {
      events.emit('refresh-right-panel');
    }

    if (isFamilyDashboard.value) {
      events.emit('reload-credit-cards');
    }
  } catch (error) {
    resetForm();
    resetStripeElement();

    const normalErrorText = util.getGraphqlError(error, false);
    const declinedErrorText = util.getGraphqlQLStripeDeclinedMessage(error);
    result.value = {
      message: normalErrorText,
      operatorNotificationError: declinedErrorText,
      type: 'error',
    };
    !showInlineError.value && showErrorPopupDialog({
      text: result.value.message,
    });
  }

  sendPushNotification();
  status.isLoading = false;
};

const setHomeAddress = () => {
  address.value = {
    streetAddress: customerInfo.value.streetAddress ?? '',
    streetAddress2: customerInfo.value.streetAddress2 ? customerInfo.value.streetAddress2 : '',
    city: customerInfo.value.city ?? '',
    state: customerInfo.value.state ?? '',
    zip: customerInfo.value.zip ?? '',
    country: customerInfo.value.country ?? '',
  };
};

const resetForm = () => {
  clearStripe();
  setHomeAddress();

  const { id, phoneNumber, firstName, lastName } = customerInfo.value;
  const holderName = id ? `${firstName} ${lastName}` : '';

  cardInfo.value = {
    callerId: id,
    holderName,
    holderPhoneNumber: phoneNumber ?? '',
  };

  selectedPlan.value = props.plan || customerInfo.value?.lastPlanName; // requested plan first, otherwise last caller plan
  result.value = {
    message: '',
    operatorNotificationError: '',
    type: '',
  };
  status.isCardAdded = false;
};

const sendPushNotification = () => {
  if (!isSelfPayForm.value) {
    return;
  }

  return pushNotificationService.sendPushNotificationOnLiveCall({
    subject: `Payment Status for ${customerFullName.value}`,
    message: result.value.operatorNotificationError || result.value.message,
    code: result.value.type === 'success' ? PushNotificationCode.ClientCardAdded : PushNotificationCode.ClientStripeError,
  });
}

watch(customerInfo, resetForm, {
  immediate: true,
});

watch(use3ds, resetStripeElement);

onMounted(resetStripeElement);
</script>

<style lang="scss">
#add-credit-card {
  .result {
    padding: 10px;
    border-radius: 10px;
    margin-top: 20px;
    margin-bottom: 10px;

    &.success {
      background: #e8f5e9;
      border: 1px solid #a5d6a7;
      color: #2e7d32;
    }

    &.error {
      background: #f9e9ea;
      border: 1px solid #eab8ba;
      color: #c32f32;
    }
  }
}
</style>
